import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';

import usePriceFormatter from './usePriceFormatter';

import { trans } from '../../helpers/translations';

import { getIsMobile } from '../../store/selectors';
import { AiAssistantProductVariant } from '../../store/typings';
import IconButton from '../IconButton';
import Translation from '../Translation';
import { Close as CloseIcon } from '../svgIcons/SvgIcons';
import styles from './VariantPickerContent.styles';

const getVariantLabel = (variant: AiAssistantProductVariant): string =>
    Object.values(variant.variant).join(', ');

type VariantPickerContentProps = {
    title: string;
    variants: AiAssistantProductVariant[];
    currency: string;
    onClose: () => void;
    onSelect: (variantId: number) => void;
};

const VariantPickerContent = ({
    title,
    variants,
    currency,
    onClose,
    onSelect,
}: VariantPickerContentProps): ReactElement => {
    const formatter = usePriceFormatter(currency);
    const isMobile = useSelector(getIsMobile);

    return (
        <>
            <div css={styles.header}>
                <p css={styles.title}>{title}</p>
                <IconButton
                    icon={CloseIcon}
                    css={styles.closeButton}
                    onClick={onClose}
                    aria-label={trans('close', null, 'Close')}
                />
            </div>
            <div css={styles.list}>
                {variants.map((productVariant) => {
                    const isSoldOut = productVariant.available_for_sale === false;
                    const label = getVariantLabel(productVariant);

                    return (
                        <button
                            key={productVariant.variant_id}
                            type="button"
                            css={styles.rowContent}
                            disabled={isSoldOut}
                            onClick={() => onSelect(productVariant.variant_id)}
                        >
                            <span css={styles.label(isSoldOut, isMobile)}>{label}</span>
                            <span css={styles.price}>
                                {isSoldOut ? (
                                    <Translation
                                        value="recommendProductSoldOut"
                                        fallback="Sold out"
                                    />
                                ) : (
                                    (formatter?.format(productVariant.price) ??
                                    `${productVariant.price}`)
                                )}
                            </span>
                        </button>
                    );
                })}
            </div>
        </>
    );
};

export default VariantPickerContent;
